import React, { useState } from 'react';
import { Button, Message, Panel, Placeholder } from 'rsuite';
import { ConfirmModal, Modal } from '@nsi-advisory-services/components';
import { useParams } from 'react-router-dom';
import { showToast } from '../utils/toast';
import PermissionGate from '../components/shared/PermissionGate';
import { useAuth } from '../contexts/AuthContext';
import TimeOffRequestCard from '../components/timeoff/TimeOffRequestCard';
import PageHeader from '../components/shared/PageHeader';
import TimeOffRequestForm from '../components/timeoff/TimeOffRequestForm';
import ApprovalActionModal from '../components/timeoff/ApprovalActionModal';
import {
  useApproveTimeOffRequest,
  useCancelTimeOffRequest,
  useRejectTimeOffRequest,
  useTimeOffRequest,
  useUpdateTimeOffRequest
} from '../hooks/useQueries';

const STALE_APPROVAL_TOAST = 'Request has already been approved or is currently being edited';

const isStaleTimeOffApprovalError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return message.includes('http 400') && message.includes('only pending requests can be approved');
};

const showApprovalToast = (type, content) => showToast(type, content, { duration: 5000 });

export default function TimeOffRequestPage() {
  const { id } = useParams();
  const auth = useAuth();

  const requestQuery = useTimeOffRequest(id, { enabled: true });
  const updateRequest = useUpdateTimeOffRequest();
  const approveRequest = useApproveTimeOffRequest();
  const rejectRequest = useRejectTimeOffRequest();
  const cancelRequest = useCancelTimeOffRequest();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const request = requestQuery.data;
  const isOwner = !!auth.employee?.id && request?.employee_id === auth.employee.id;

  const submit = async (payload) => {
    await updateRequest.mutateAsync({ id, ...payload });
    setEditOpen(false);
    await requestQuery.refetch();
  };

  const approve = () => setApproveOpen(true);
  const reject = () => setRejectOpen(true);

  const submitApprove = async (approval_reason) => {
    try {
      await approveRequest.mutateAsync({ id, approval_reason });
      setApproveOpen(false);
      await requestQuery.refetch();
    } catch (error) {
      if (isStaleTimeOffApprovalError(error)) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        setApproveOpen(false);
        await requestQuery.refetch();
        return;
      }

      showApprovalToast('error', String(error?.message || error || 'Unable to approve request'));
    }
  };

  const submitReject = async (approval_reason) => {
    await rejectRequest.mutateAsync({ id, approval_reason });
    setRejectOpen(false);
    await requestQuery.refetch();
  };

  const cancel = async () => {
    await cancelRequest.mutateAsync(id);
    setConfirmCancel(false);
    await requestQuery.refetch();
  };

  if (requestQuery.isLoading) return <Placeholder.Paragraph rows={6} active />;

  if (requestQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(requestQuery.error?.message || requestQuery.error)}
      </Message>
    );
  }

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Time Off', to: '/time-off' },
          { label: 'My Time Off', to: '/time-off/requests' },
          { label: 'Request' },
        ]}
        icon="bi-calendar-event"
        title="Time-Off Request"
      />

      <Panel bordered>
        <TimeOffRequestCard
          request={request}
          onWithdraw={() => setConfirmCancel(true)}
        />

        {String(request?.status).toLowerCase() === 'approved' || String(request?.status).toLowerCase() === 'rejected' ? (
          <Panel bordered className="mt-3" header="Approval">
            <div className="d-flex flex-column gap-1">
              <div>
                <strong>Status:</strong> {String(request?.status)}
              </div>
              {request?.approved_at ? (
                <div>
                  <strong>Decision time:</strong> {new Date(request.approved_at).toLocaleString()}
                </div>
              ) : null}
              {request?.approver_first_name || request?.approver_last_name ? (
                <div>
                  <strong>By:</strong> {`${request.approver_first_name || ''} ${request.approver_last_name || ''}`.trim()}
                  {request?.approver_email ? ` (${request.approver_email})` : ''}
                </div>
              ) : null}
              {request?.approval_reason ? (
                <div>
                  <strong>Reason:</strong> {request.approval_reason}
                </div>
              ) : null}
            </div>
          </Panel>
        ) : null}

        <div className="d-flex flex-wrap gap-2 mt-3">
          <Button appearance="primary" onClick={() => setEditOpen(true)} disabled={String(request?.status).toLowerCase() !== 'pending'}>
            <i className="bi bi-pencil me-2" />
            Edit
          </Button>

          {isOwner ? (
            <Button
              appearance="ghost"
              onClick={() => setConfirmCancel(true)}
              disabled={String(request?.status).toLowerCase() !== 'pending'}
            >
              <i className="bi bi-x-circle me-2" />
              Cancel
            </Button>
          ) : null}

          <PermissionGate anyOf={['approve_time_off']} fallback={null}>
            <Button appearance="primary" color="green" onClick={approve} disabled={String(request?.status).toLowerCase() !== 'pending'}>
              <i className="bi bi-check-circle me-2" />
              Approve
            </Button>
            <Button appearance="ghost" color="red" onClick={reject} disabled={String(request?.status).toLowerCase() !== 'pending'}>
              <i className="bi bi-x-circle me-2" />
              Reject
            </Button>
          </PermissionGate>
        </div>
      </Panel>

      <ApprovalActionModal
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        request={request}
        action="approve"
        onSubmit={submitApprove}
        loading={approveRequest.isPending}
      />

      <ApprovalActionModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        request={request}
        action="reject"
        onSubmit={submitReject}
        loading={rejectRequest.isPending}
      />

      <Modal open={editOpen} onClose={() => setEditOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Edit Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TimeOffRequestForm
            initialValue={request}
            onSubmit={submit}
            submitLabel="Save"
            loading={updateRequest.isPending}
            error={updateRequest.error}
          />
        </Modal.Body>
      </Modal>

      {isOwner ? (
        <ConfirmModal
          open={confirmCancel}
          title="Withdraw Request"
          body="Withdraw this time-off request?"
          confirmText="Withdraw"
          cancelText="Close"
          danger
          loading={cancelRequest.isPending}
          onConfirm={cancel}
          onCancel={() => setConfirmCancel(false)}
        />
      ) : null}
    </div>
  );
}
